import { useId } from "react";

import { cn } from "@/lib/utils";

interface VsMeshGradientProps {
  className?: string;
  /**
   * SVG `viewBox`. Defaults to `"0 0 100 100"` (the full hero mesh). Pass an
   * extended range (e.g. `"0 -4.4 100 4.4"`) to render the strip *above* the
   * hero so the action bar can show a continuation of the mesh — the bottom
   * of that strip and the top of the hero both land at mesh y=0, which makes
   * the action-bar / hero seam pixel-perfect.
   */
  viewBox?: string;
}

/**
 * SVG mesh gradient — five soft colour blobs blended with a heavy gaussian
 * blur on top of a base fill. Drop-in replacement for
 * `VS_HERO_GRADIENT` (which is a hard linear gradient); compose `<VsMeshGradient />`
 * inside any positioning context with `overflow-hidden`. Sits below the
 * grain overlay so the noise composites on top.
 *
 * Why SVG + blur instead of stacked `radial-gradient`s: the blur pass blends
 * the ellipses into organic, non-elliptical shapes — closer to the matt
 * "mesh gradient" reference while staying ~1 KB and animation-friendly.
 */
export function VsMeshGradient({
  className,
  viewBox = "0 0 100 100",
}: VsMeshGradientProps) {
  // Per-instance filter id so multiple `<VsMeshGradient />` on the same page
  // (action bar + hero) don't collide on `url(#…)` references.
  const blurId = useId();
  return (
    <svg
      aria-hidden
      preserveAspectRatio="none"
      viewBox={viewBox}
      className={cn(
        "pointer-events-none absolute inset-0 h-full w-full",
        className
      )}
    >
      <defs>
        <filter id={blurId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="14" />
        </filter>
      </defs>
      {/* Extends above y=0 so the action-bar viewBox (which renders the
          strip above the hero) still has a base fill behind it. */}
      <rect x="0" y="-20" width="100" height="120" fill="#3a1660" />
      <g filter={`url(#${blurId})`}>
        {/* royal blue — anchored upper-right, vertical centre aligned with
            the middle of the VISIONARIES wordmark (hero-y ≈ 386 / 820 ≈ 47%) */}
        <ellipse cx="88" cy="44" rx="56" ry="48" fill="#0a1ee8" />
        {/* royal-blue cap — small top-right halo so the colour reaches y=0
            and stays continuous with the action bar's blue end stop */}
        <ellipse cx="96" cy="0" rx="22" ry="14" fill="#0a1ee8" />
        {/* deep indigo — bottom-right corner, anchors the dark band along the
            bottom edge so the floor of the gradient is darker than the body */}
        <ellipse cx="98" cy="100" rx="48" ry="48" fill="#1a0a3a" />
        {/* deep purple — bottom-left, anchors the cool floor on the left */}
        <ellipse cx="22" cy="100" rx="44" ry="36" fill="#2c1260" />
        {/* rich purple — mid-left, the transition zone from pink to purple */}
        <ellipse cx="6" cy="56" rx="38" ry="34" fill="#5e2aa0" />
        {/* soft pink-magenta — upper-left, hand-off from peach to purple */}
        <ellipse cx="4" cy="30" rx="34" ry="26" fill="#d860a0" />
        {/* peach / coral — top-left corner accent (figma: warm salmon) */}
        <ellipse cx="14" cy="14" rx="32" ry="22" fill="#ff8a7a" />
        {/* peach cap — small top-left halo so the colour still reaches y=0
            and stays continuous with the action bar's peach start stop */}
        <ellipse cx="6" cy="0" rx="20" ry="12" fill="#ff8a7a" />
        {/* top bridge — fills the gap between the peach and blue caps so the
            top edge stays continuously coloured (no dark patches) */}
        <ellipse cx="58" cy="0" rx="32" ry="18" fill="#7c2c98" />
      </g>
    </svg>
  );
}
